<template>
   <div class="d-flex flex-column gap-2 w-100" data-testid="breadcrumbs">
    <!-- Row 1: Breadcrumb Trail -->
    <div class="d-flex align-center">
      <span class="breadcrumb-link" @click="$emit('back')">
        {{ glCodeText }}
      </span>
      <template v-if="glCodeInfo">
        <span class="breadcrumb-separator">/</span>
        <span class="breadcrumb-glcode">
          {{ getDisplayCode(glCodeInfo) }}
        </span>
      </template>
      <template v-else-if="isRefreshing">
        <span cla ss="breadcrumb-separator">/</span>
        <span class="breadcrumb-shimmer breadcrumb-shimmer--code"></span>
      </template>
    </div>

    <!-- Row 2: Header Title & Actions -->
    <div class="d-flex align-center justify-space-between flex-wrap gap-3">
      <div class="d-flex align-center flex-wrap gap-3 breadcrumbs__left" data-testid="breadcrumbs__left-pane">
        <!-- Back Button -->
        <div class="back-btn-wrapper" data-testid="breadcrumbs__back-button" @click="$emit('back')">
          <v-icon small color="#374151">mdi-arrow-left</v-icon>
        </div>

        <!-- Title -->
        <div class="header-title">
          <template v-if="glCodeInfo">
            {{ glCodeInfo }}
          </template>
          <template v-else-if="isRefreshing">
            <span class="breadcrumb-shimmer breadcrumb-shimmer--title"></span>
          </template>
        </div>

        <!-- Status Pill -->
        <template v-if="statusVariant">
          <StatusIndicator
            :variant="statusVariant"
            :custom-label="statusLabel"
            size="sm"
            outlined
            style="width: auto;"
            data-testid="breadcrumbs__status-pill"
            cornerRadius="8px"
          />
        </template>

        <!-- Refresh Button -->
        <v-tooltip v-if="showRefresh" bottom content-class="tooltip-bottom">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              v-bind="attrs"
              v-on="on"
              :disabled="isRefreshing || isActionInProgress"
              @click="$emit('refresh')"
              class="refresh-btn"
              data-testid="breadcrumbs__refresh-button"
            >
              <v-icon :class="{ 'refresh-spinning': isRefreshing }" small>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Refresh</span>
        </v-tooltip>
      </div>

      <div
        v-if="hasRightControls"
        class="d-flex align-center gap-2 breadcrumbs__right"
        data-testid="breadcrumbs__right-pane"
      >
        <!-- Role Display: Chip-based (when SOD enabled or single role) -->
        <div 
          v-if="showRoleChip" 
          class="role-chip"
          data-testid="breadcrumbs__role-chip"
        >
          <img src="../../../../public/img/icons/User.svg" alt="" class="role-chip__icon">
          <span class="role-chip__label">{{ roleChipLabel }}</span>
        </div>

        <!-- Role Selector: Dropdown (when SOD disabled and multiple roles) -->
        <div v-if="showRoleSelector" class="role-selector__wrapper">
          <v-menu
            v-model="roleSelectorMenuOpen"
            left
            offset-y
            transition="slide-y-transition"
            :disabled="isActionInProgress"
            data-testid="breadcrumbs__role-selector-menu"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                class="role-selector__activator cursor-pointer"
                :class="{ 'role-selector__activator--disabled': isActionInProgress }"
                v-bind="attrs"
                v-on="isActionInProgress ? {} : on"
                data-testid="breadcrumbs__role-selector"
              >
                <div class="role-chip__content">
                  <img src="../../../../public/img/icons/User.svg" alt="">
                  <span class="role-chip__label font-weight-semibold">{{ roleSelectorLabel }}</span>
                </div>
                <v-icon small class="role-selector__chevron">mdi-chevron-down</v-icon>
              </div>
            </template>
            <v-card class="role-selector__menu">
              <v-list class="pa-0 d-flex flex-column gap-1" data-testid="breadcrumbs__role-options-list">
                <v-list-item
                  v-for="option in ruRoleOptions"
                  :key="option.value"
                  @click="handleRoleSelect(option.value)"
                  class="role-selector__item"
                  :class="{ 'role-selector__item--active': option.value === selectedRuRole }"
                  data-testid="breadcrumbs__role-option"
                >
                  <div class="role-chip__content">
                    <span class="role-chip__label">{{ option.label }}</span>
                  </div>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </div>

        <!-- Request Commentary Buttons -->
        <template v-if="requestCommentaryOptions">
          <!-- Request Local Commentary -->
          <v-tooltip 
            v-if="requestCommentaryOptions.showLocalRequest" 
            bottom 
            content-class="tooltip-bottom"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                :class="['breadcrumbs__request-btn', { 'breadcrumbs__request-btn--disabled': !requestCommentaryOptions.localEnabled || isActionInProgress }]"
                @click="requestCommentaryOptions.localEnabled && !isActionInProgress && $emit('request-commentary', 'requestlocal')"
                data-testid="breadcrumbs__request-local-commentary"
              >
                <img 
                  src="/img/icons/request-commentary.svg" 
                  alt="Request Commentary"
                  class="breadcrumbs__request-icon"
                >
              </div>
            </template>
            <span>Request Commentary</span>
          </v-tooltip>

          <!-- Request Regional Commentary -->
          <v-tooltip 
            v-if="requestCommentaryOptions.showRegionalRequest" 
            bottom 
            content-class="tooltip-bottom"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                :class="['breadcrumbs__request-btn', { 'breadcrumbs__request-btn--disabled': !requestCommentaryOptions.regionalEnabled || isActionInProgress }]"
                @click="requestCommentaryOptions.regionalEnabled && !isActionInProgress && $emit('request-commentary', 'requestregional')"
                data-testid="breadcrumbs__request-regional-commentary"
              >
                <img 
                  src="/img/icons/request-commentary.svg" 
                  alt="Request Regional Commentary"
                  class="breadcrumbs__request-icon"
                >
              </div>
            </template>
            <span>Request Commentary</span>
          </v-tooltip>
        </template>

        <template v-if="conversationOptions">
          <template v-if="isConversationEnabled">
            <lb-conversation
              data-testid="breadcrumbs__conversation"
              heading="Conversation"
              width="600"
              imageWidth="24"
              :recurring="false"
              :recurringText="''"
              :closed="Boolean(conversationOptions.closed)"
              :highlight="Boolean(conversationOptions.highlight)"
              :notify="Boolean(conversationOptions.notify)"
              :getapi="conversationOptions.getApi"
              :sendapi="conversationOptions.sendApi"
              :readapi="conversationOptions.readApi"
              :showprofile="false"
              :filter="{ reporting_type: reportingType }"
              @markread="$emit('mark-conversation-read')"
            />
          </template>
          <template v-else>
            <v-tooltip bottom content-class="tooltip-bottom">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="breadcrumbs__conversation-disabled"
                  data-testid="breadcrumbs__conversation-disabled"
                  v-bind="attrs"
                  v-on="on"
                >
                  <img src="/img/icons/conversation.svg" alt="">
                  <v-icon small>mdi-comment-text-multiple</v-icon>
                </v-btn>
              </template>
              <span>{{ conversationOptions.tooltip }}</span>
            </v-tooltip>
          </template>
        </template>

        <lb-activitylog
          v-if="activityLogOptions"
          data-testid="breadcrumbs__activity-log"
          :config="activityLogOptions.config"
          :url="activityLogOptions.url"
          :filter="{ reporting_type: reportingType }"
          :reporting-type="reportingType"
          class="icon-24"
          :image-width="20"
        />
      </div>
    </div>
  </div>
</template>

<script>
import StatusIndicator from './StatusIndicator.vue';
import { STATUS_VARIANTS, COMMENTARY_STAGE_VARIANT_MAP, COMMENTARY_STAGE_LABEL_MAP } from '../utils/statusConstants';

export default {
  name: 'Breadcrumbs',
  components: {
    StatusIndicator,
  },
  props: {
    glCodeText: {
      type: String,
      required: true,
    },
    glCodeInfo: {
      type: String,
      default: '',
    },
    activeCommentary: {
      type: Object,
      required: true,
    },
    getStatusLabel: {
      type: Function,
      required: true,
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
    showRefresh: {
      type: Boolean,
      default: false,
    },
    conversationOptions: {
      type: Object,
      default: null,
    },
    activityLogOptions: {
      type: Object,
      default: null,
    },
    /** Reporting type filter - 'bs' (Balance Sheet), 'pl' (Profit & Loss), or 'both' */
    reportingType: {
      type: String,
      default: 'pl',
    },
    /**
     * Request commentary options for showing request buttons.
     * @type {Object|null}
     * @property {Boolean} showLocalRequest - Show request local commentary button
     * @property {Boolean} showRegionalRequest - Show request regional commentary button
     * @property {Boolean} localEnabled - Enable local request button
     * @property {Boolean} regionalEnabled - Enable regional request button
     */
    requestCommentaryOptions: {
      type: Object,
      default: null,
    },
    /**
     * Custom status to display overrides stage-based status
     * @property {String} variant - Status variant
     * @property {String} label - Status label
     */
    customStatus: {
      type: Object,
      default: null,
    },
    /** Whether SOD (Segregation of Duties) is enabled */
    isSodEnabled: {
      type: Boolean,
      default: true,
    },
    /** Available role options for dropdown */
    ruRoleOptions: {
      type: Array,
      default: () => [],
    },
    /** Currently selected role */
    selectedRuRole: {
      type: String,
      default: null,
    },
    /** Whether all users are assigned */
    alluserassigned: {
      type: Boolean,
      default: true,
    },
    /** User role flags */
    ispreparer: {
      type: Boolean,
      default: false,
    },
    isapprover: {
      type: Boolean,
      default: false,
    },
    isregionalapprover: {
      type: Boolean,
      default: false,
    },
    ismanager: {
      type: Boolean,
      default: false,
    },
    isviewonly: {
      type: Boolean,
      default: false,
    },
    /** Whether an action (save, submit, approve, reject) is in progress */
    isActionInProgress: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      roleSelectorMenuOpen: false,
    };
  },
  computed: {
    stage() {
      return this.activeCommentary && typeof this.activeCommentary.__stage === 'number'
        ? this.activeCommentary.__stage
        : null;
    },
    stageClass() {
      if (typeof this.stage === 'number' || !isNaN(Number(this.stage))) {
        return this.stage;
      }
      return 'default';
    },
    statusVariant() {
      if (this.customStatus) return this.customStatus.variant;
      if (this.stage === null) return null;
      return COMMENTARY_STAGE_VARIANT_MAP[String(this.stage)] || STATUS_VARIANTS.UNKNOWN;
    },
    statusLabel() {
      if (this.customStatus) return this.customStatus.label;
      if (this.stage === null) return '';
      const config = COMMENTARY_STAGE_LABEL_MAP[String(this.stage)];
      if (config) {
        // Use fallback directly to show concise status ("Approved") instead of translation key
        return config.fallback;
      }
      return '';
    },
    hasRightControls() {
      return Boolean(this.conversationOptions || this.activityLogOptions || this.requestCommentaryOptions || this.showRoleSelector || this.showRoleChip);
    },
    isConversationEnabled() {
      if (!this.conversationOptions) {
        return false;
      }
      if (typeof this.conversationOptions.enabled === 'boolean') {
        return this.conversationOptions.enabled;
      }
      return true;
    },
    /**
     * Whether to show the role chip (read-only display).
     * Shows when SOD is enabled OR there is only one role option.
     */
    showRoleChip() {
      return (this.isSodEnabled || (this.ruRoleOptions || []).length <= 1) && this.hasAnyRole;
    },
    /**
     * Whether user has any role assigned.
     */
    hasAnyRole() {
      return this.ispreparer || this.isapprover || this.isregionalapprover || this.ismanager || this.isviewonly;
    },
    /**
     * Label for the current role chip.
     */
    roleChipLabel() {
      if (this.isregionalapprover) {
        return this.$nova?.labelsName?.('cycle_page', 'commentary_regional_approver_label', 'Regional Approver') ?? 'Regional Approver';
      }
      if (this.ispreparer) {
        return this.$nova?.labelsName?.('cycle_page', 'commentary_local_preparer_label', 'Local Preparer') ?? 'Local Preparer';
      }
      if (this.isapprover) {
        return this.$nova?.labelsName?.('cycle_page', 'commentary_local_approver_label', 'Local Approver') ?? 'Local Approver';
      }
      if (this.ismanager) {
        return 'Manager';
      }
      if (this.isviewonly) {
        return 'View Only';
      }
      return '';
    },
    /**
     * Whether to show the role selector dropdown.
     * Shows when SOD is disabled and there are multiple role options.
     */
    showRoleSelector() {
      return !this.isSodEnabled && (this.ruRoleOptions || []).length > 1;
    },
    /**
     * Label for the currently selected role in the dropdown.
     */
    roleSelectorLabel() {
      const selected = (this.ruRoleOptions || []).find(option => option.value === this.selectedRuRole);
      if (selected) {
        return selected.label;
      }
      const firstOption = (this.ruRoleOptions || [])[0];
      return firstOption ? firstOption.label : this.$nova?.labelsName?.('cycle_page', 'commentary_role_placeholder', 'Select Role') ?? 'Select Role';
    },
  },
  methods: {
    getDisplayCode(info) {
      if (!info) return '';
      // Split by " - " and return the first part (the code)
      const parts = info.split(' - ');
      return parts.length > 0 ? parts[0] : info;
    },
    /**
     * Handles role selection from dropdown.
     * Emits update event to parent for state management.
     */
    handleRoleSelect(roleValue) {
      this.$emit('update:selected-ru-role', roleValue);
      this.roleSelectorMenuOpen = false;
    },
  },
};
</script>

<style scoped lang="scss">
/* Breadcrumb typography base styles */
.breadcrumb-link,
.breadcrumb-glcode,
.breadcrumb-separator {
  font-weight: 400;
  font-style: normal;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  text-transform: capitalize;
}

.breadcrumb-link {
  cursor: pointer;
  color: #828285;
}

.breadcrumb-separator {
  color: #828285;
  margin: 0 4px;
}

.breadcrumb-current {
  font-weight: 600;
}

.breadcrumb-glcode {
  color: #828285;
}

.header-title {
  font-weight: 600;
  font-size: 18px;
  line-height: 26px;
  color: #2c2c35;
  text-transform: capitalize;
}

.back-btn-wrapper {
  width: 32px;
  height: 32px;
  background: #f7f7f7;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #e5e5e5;
  }
}

.breadcrumbs__left {
  gap: 12px;
}

.breadcrumbs__right {
  gap: var(--space-6, 12px);
}

.refresh-btn {
  color: #6b7280;
  
  &:hover {
    color: #374151;
  }
}

.breadcrumbs__conversation-disabled {
  color: #6b7280;
  border: 1px solid transparent;
  border-radius: 4px;
  background-color: #f7f7f7;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    color: #374151;
    background-color: #e5e5e5;
  }
}

.breadcrumbs__action-btn {
  color: #6b7280;
  border: 1px solid transparent;
  border-radius: 4px;
  background-color: #f7f7f7;
  transition: color 0.2s, background-color 0.2s;

  &:hover:not(:disabled) {
    color: #374151;
    background-color: #e5e5e5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.breadcrumbs__request-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  &--disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.breadcrumbs__request-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.refresh-spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Shimmer loading animation */
.breadcrumb-shimmer {
  display: inline-block;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.breadcrumb-shimmer--code {
  width: 80px;
  height: 16px;
}

.breadcrumb-shimmer--title {
  width: 200px;
  height: 22px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Role Selector Dropdown Styles */
.role-selector__wrapper {
  width: 214px;
}

.role-selector__activator {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 214px;
  height: 40px;
  padding: 8px 12px 8px 16px;
  border-radius: 10px;
  border: 1px solid var(--Text-Grey-Weak, #B2B2B4);
  background: var(--background-gray-weak, #FFFFFF);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.role-selector__activator:hover {
  border-color: var(--color-primary, #1976d2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.role-selector__activator--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.role-selector__chevron {
  color: var(--color-status-card-text, #828285);
}

.role-selector__menu {
  min-width: 214px;
  border-radius: 10px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.08), 0 1px 4px 0 var(--opacity-200, rgba(5, 5, 6, 0.16)), 0 1px 4px 0 var(--opacity-100, rgba(5, 5, 6, 0.08));
  border: 1px solid var(--grey-300, #B2B2B4);
  padding: 12px;
}

.role-selector__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  color: var(--grey-500) !important;
  transition: background-color 0.2s ease;
  border-radius: var(--radius-sm, 8px);
}

.role-selector__item:hover {
  background: var(--grey-50, #F7F7F7);
  color: var(--grey-900, #2C2C35);
}

.role-selector__item--active {
  background: var(--grey-50, #F7F7F7);
  color: var(--grey-900, #2C2C35) !important;
}

.role-chip__content {
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
}

/* Role Chip (read-only display) */
.role-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 10px;
  border: 1px solid var(--Text-Grey-Weak, #B2B2B4);
  background: var(--background-gray-weak, #FFFFFF);
}

.role-chip__icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.role-chip__label {
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
// ::v-deep(.icon-24) img{
//   width: 24px;
//   height: 24px;
// }
</style>
